/**
 * DashboardPageV2 — redesigned admin dashboard.
 *
 * Direct port of the prototype's dashboard block (cms-redesign-mockup.html
 * ~line 405–700). Same Redux selectors as v1 (state.dashboard.{stats,
 * appStore, loadingStats, loadingAppStore, error}); only the presentation
 * changes.
 *
 * Layout (top → bottom):
 *   1. Greeting + date
 *   2. Hero row — 3 cards: Completed Meditations · Most Played · Active Users
 *   3. "Available Content" — six content cards (3 columns × 2 rows)
 *   4. "App Store Reviews" — Apple + Google panels with avg, distribution
 *      bars (additive — not in the static prototype but the data exists),
 *      and a list of recent reviews
 *
 * Two intentional additions on top of the prototype:
 *   • Per-rating distribution bars under the big rating — uses
 *     `appStore.{store}.rating_counts` which the API already returns.
 *   • The date strip doubles as a refresh trigger (subtle hover affordance).
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDashboardStats,
  fetchAppStoreRatings,
} from '../../../actions/dashboardActions';
import SessionHelper from '../../Helper/SessionHelper';
import { BrandIcon, IconButton, Tooltip, cn } from '../../redesign';

/** Refresh glyph — the brand pack doesn't include a clockwise circular-arrow
 *  icon (closest is `replay`, which is the wrong direction), so this stays
 *  as a tight inline SVG. 14px to fit the IconButton size="sm" 28px square. */
const RefreshIcon = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function greeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Matches the prototype's `.dash-greeting .date` format:
 *  "Thursday · May 21, 2026" — weekday, dot separator, month day, year. */
function today() {
  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  }).formatToParts(new Date());
  const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
  return `${get('weekday')} · ${get('month')} ${get('day')}, ${get('year')}`;
}

function firstName(user) {
  // SessionHelper.currentUser() returns { admin_name, admin_email, ... }
  if (!user) return 'there';
  const name = user.admin_name || user.admin_email || '';
  return name.split(/\s+|@/)[0] || 'there';
}

function formatNum(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString();
}

function formatRating(r) {
  const num = Number(r);
  if (!Number.isFinite(num)) return '—';
  return num.toFixed(2);
}

/** YYYY-MM-DD or ISO → "May 12, 2026". Falls back to the raw string if it
 *  can't be parsed (some store APIs return non-ISO dates). */
function formatReviewDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/** Amber Unicode stars — matches prototype `.stars` (amber `#F5A623`).
 *  Renders 5 chars with the filled ones first. */
function StarsText({ rating, size = 15, letterSpacing = 1.5 }) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return (
    <span
      aria-label={`${n} out of 5 stars`}
      style={{
        color: '#F5A623',
        fontSize: size,
        letterSpacing: `${letterSpacing}px`,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {'★★★★★'.slice(0, n)}
      <span style={{ color: '#E8E0D5' }}>{'★★★★★'.slice(n)}</span>
    </span>
  );
}

/* ─── Section title — `.dash-body .section-title` (prototype 406–413).
 *     18px Asap Condensed 700 navy, letter-spacing -0.01em, margin 28 0 12. */
function SectionTitle({ children }) {
  return (
    <h2
      className="font-condensed font-bold text-navy"
      style={{
        fontSize: 18,
        letterSpacing: '-0.01em',
        margin: '28px 0 12px',
        paddingLeft: 2,
      }}
    >
      {children}
    </h2>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   HERO ROW
   ═══════════════════════════════════════════════════════════════════════ */

/** `.hero-card` (prototype 422–429). */
function HeroCard({ children, className }) {
  return (
    <div
      className={cn('bg-surface border border-sand flex flex-col min-w-0', className)}
      style={{ borderRadius: 12, padding: '18px 20px' }}
    >
      {children}
    </div>
  );
}

/** `.hero-head` (430–460). */
function HeroHead({ icon, iconTone = 'teal', title, hint, window }) {
  const toneClass = {
    teal:  'bg-teal-green-soft text-teal-green-dark',
    coral: 'bg-coral-soft text-coral-dark',
    navy:  'text-navy',
  }[iconTone];
  // Navy tone uses 7% navy bg literal — Tailwind's bg-navy/10 would do but
  // the prototype uses #2C3E5012 (≈7% alpha), so be exact.
  const navyToneStyle = iconTone === 'navy' ? { background: '#2C3E5012' } : undefined;
  return (
    <div className="flex items-center" style={{ gap: 10, marginBottom: 14 }}>
      <span
        className={cn('grid place-items-center shrink-0', toneClass)}
        style={{ width: 30, height: 30, borderRadius: 8, ...navyToneStyle }}
      >
        {icon}
      </span>
      <h3
        className="font-condensed font-bold text-navy flex-1 min-w-0 inline-flex items-center"
        style={{ fontSize: 14, letterSpacing: '-0.005em', gap: 4 }}
      >
        <span>{title}</span>
        {hint && (
          // `.hint` (prototype line 457–460): 12px text-muted, margin-left 2,
          // cursor: help. Custom styled Tooltip (dark navy bubble) instead of
          // the native browser title — appears immediately with brand styling
          // and works regardless of system font glyph coverage.
          <Tooltip content={hint} side="bottom">
            <span
              className="inline-flex text-text-muted cursor-help"
              style={{ marginLeft: 2 }}
              tabIndex={0}
              aria-label={hint}
            >
              <BrandIcon name="info" size={13} />
            </span>
          </Tooltip>
        )}
      </h3>
      {window && (
        <span
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}
        >
          {window}
        </span>
      )}
    </div>
  );
}

/** `.hero-main .big-num` (462–467) — 40px Asap Condensed.
 *  Active Users variant ups this to 52px and centers. */
function BigNum({ children, size = 40 }) {
  return (
    <div
      className="font-condensed font-bold text-navy"
      style={{ fontSize: size, letterSpacing: '-0.02em', lineHeight: 1 }}
    >
      {children}
    </div>
  );
}

/** `.hero-main .caption` (468–471). */
function HeroCaption({ children }) {
  return (
    <div className="text-text-light" style={{ fontSize: 12, marginTop: 6 }}>
      {children}
    </div>
  );
}

/* ─── Completed Meditations ──────────────────────────────────────────── */

function CompletedMeditationsCard({ data, loading }) {
  const d = data || {};
  return (
    <HeroCard>
      <HeroHead
        icon={<BrandIcon name="checkmark" size={16} />}
        iconTone="teal"
        title="Completed Meditations"
      />
      <div style={{ marginBottom: 14 }}>
        <BigNum>{loading ? '…' : formatNum(d.total)}</BigNum>
        <HeroCaption>Total completions</HeroCaption>
      </div>
      {/* `.hero-breakdown` (472–486). margin-top auto pushes it to the bottom. */}
      <div
        className="grid grid-cols-2 border-t border-sand"
        style={{ marginTop: 'auto', gap: 12, paddingTop: 14 }}
      >
        <BreakdownTile
          value={loading ? '…' : formatNum(d.regular_sessions)}
          label="Regular Sessions"
        />
        <BreakdownTile
          value={loading ? '…' : formatNum(d.quick_tap_sessions)}
          label="Quick Taps"
        />
      </div>
    </HeroCard>
  );
}

function BreakdownTile({ value, label }) {
  return (
    <div>
      <div
        className="font-condensed font-bold text-navy"
        style={{ fontSize: 18, lineHeight: 1 }}
      >
        {value}
      </div>
      <div className="text-text-muted" style={{ fontSize: 11, marginTop: 3 }}>
        {label}
      </div>
    </div>
  );
}

/* ─── Most Played ────────────────────────────────────────────────────── */

function MostPlayedCard({ data, loading }) {
  return (
    <HeroCard>
      <HeroHead
        icon={<BrandIcon name="play" size={14} />}
        iconTone="coral"
        title="Most Played Content"
      />
      <MostPlayedRow eyebrow="Top Session"   item={data?.session}   loading={loading} />
      <MostPlayedRow eyebrow="Top Quick Tap" item={data?.quick_tap} loading={loading} />
    </HeroCard>
  );
}

function MostPlayedRow({ eyebrow, item, loading }) {
  return (
    <div style={{ marginBottom: 14 }} className="last:mb-0">
      {/* `.mp-eyebrow` (498–504). */}
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10, letterSpacing: '0.08em', marginBottom: 6 }}
      >
        {eyebrow}
      </div>
      {loading ? (
        <div className="text-text-light italic" style={{ fontSize: 12 }}>Loading…</div>
      ) : item ? (
        // `.mp-item` (505–509): grid 44 1fr, gap 10.
        <div
          className="grid items-center min-w-0"
          style={{ gridTemplateColumns: '44px 1fr', gap: 10 }}
        >
          {/* `.mp-thumb` (510–516): 44×44, rounded-md, cream bg, NO border. */}
          <div
            className="bg-cream shrink-0"
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              backgroundImage: item.image_url ? `url('${item.image_url}')` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="min-w-0">
            {/* `.mp-title` (518–521). */}
            <div
              className="font-semibold text-navy truncate"
              style={{ fontSize: 13 }}
            >
              {item.name}
            </div>
            {/* `.mp-plays` (522–527). */}
            <div
              className="flex items-center text-text-muted"
              style={{ fontSize: 11.5, gap: 5, marginTop: 3 }}
            >
              <BrandIcon name="play" size={11} />
              <span>{formatNum(item.views ?? 0)} plays</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-text-light italic" style={{ fontSize: 12 }}>No data yet.</div>
      )}
    </div>
  );
}

/* ─── Active Users ───────────────────────────────────────────────────── */

function ActiveUsersCard({ value, loading }) {
  return (
    <HeroCard>
      <HeroHead
        icon={<BrandIcon name="edit_account" size={16} />}
        iconTone="navy"
        title="Active Users"
        hint="Users active in the app in the past 28 days"
        window="28 days"
      />
      {/* `.hero-card.active-users .hero-main` (487–493): centered column, flex 1,
       *  big number 52px. */}
      <div
        className="flex flex-col items-center justify-center text-center"
        style={{ flex: 1, margin: '12px 0' }}
      >
        <BigNum size={52}>{loading ? '…' : formatNum(value)}</BigNum>
        <HeroCaption>Unique active users</HeroCaption>
      </div>
    </HeroCard>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   AVAILABLE CONTENT — 6 cards in a 3-column grid
   ═══════════════════════════════════════════════════════════════════════ */

/** Some content-statistics keys come back as `{ total, free, premium }`
 *  objects; others as plain numbers. v1 (`ContentStats.js:29`) handles both. */
function statTotal(v) {
  if (v == null) return null;
  if (typeof v === 'object') return v.total;
  return v;
}

/** `.c-card` (535–550). Larger card, hover lifts + teal border. */
function ContentCard({ label, icon, value, loading }) {
  const total = statTotal(value);
  const breakdown =
    value && typeof value === 'object' && (value.free != null || value.premium != null)
      ? value
      : null;
  return (
    <div
      className={cn(
        'bg-surface border border-sand flex flex-col min-w-0 cursor-pointer',
        'hover:border-teal-green hover:-translate-y-px',
        'hover:shadow-[0_6px_16px_#75a29320]',
      )}
      style={{
        borderRadius: 12,
        padding: '16px 18px',
        gap: 10,
        transition: 'all 180ms ease',
      }}
    >
      {/* `.c-head` (551–553). */}
      <div className="flex items-center" style={{ gap: 10 }}>
        {/* `.c-icon` (554–562): 30×30, cream bg, rounded-sm (4px), teal-d. */}
        <span
          className="grid place-items-center shrink-0 bg-cream text-teal-green-dark"
          style={{ width: 30, height: 30, borderRadius: 4 }}
        >
          <BrandIcon name={icon} size={16} />
        </span>
        {/* `.c-card h4` (563–568): 14px Asap Condensed 700 navy. */}
        <h4
          className="font-condensed font-bold text-navy flex-1 min-w-0"
          style={{ fontSize: 14, letterSpacing: '-0.005em' }}
        >
          {label}
        </h4>
      </div>
      {/* `.c-num` (569–574): 32px Asap Condensed 700 navy line-height 1. */}
      <div
        className="font-condensed font-bold text-navy"
        style={{ fontSize: 32, letterSpacing: '-0.02em', lineHeight: 1 }}
      >
        {loading ? '…' : formatNum(total)}
      </div>
      {/* `.c-bd` (575–587): two pill chips, free (green tint) + prem (coral). */}
      {breakdown && !loading && (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          <Chip kind="free">{formatNum(breakdown.free)} Free</Chip>
          <Chip kind="prem">{formatNum(breakdown.premium)} Premium</Chip>
        </div>
      )}
    </div>
  );
}

/** `.bd-chip` (578–587): Asap Condensed 10/700 uppercase, padding 3 8, pill.
 *  • `.free`  — green tint #87A87820 / text #5B7F4E (one-off colors, kept as
 *               arbitrary values since they aren't in the brand token set).
 *  • `.prem`  — coral-soft bg + coral-dark text (tokens, so they track any
 *               future :root changes automatically). */
function Chip({ kind, children }) {
  return (
    <span
      className={cn(
        'font-condensed font-bold uppercase rounded-pill',
        kind === 'free'
          ? 'bg-[#87A87820] text-[#5B7F4E]'
          : 'bg-coral-soft text-coral-dark',
      )}
      style={{
        fontSize: 10,
        letterSpacing: '0.06em',
        padding: '3px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   APP STORE REVIEWS
   ═══════════════════════════════════════════════════════════════════════ */

const STORE_URLS = {
  apple:  'https://apps.apple.com/us/app/the-tapping-solution/id1419815487',
  google: 'https://play.google.com/store/apps/details?id=com.datechnologies.tappingsolution',
};

/** Normalize `rating_counts` into a consistent `{1..5}` map. The Laravel API
 *  currently serializes the histogram as a zero-indexed JSON array
 *  `[c1, c2, c3, c4, c5]` even though it's declared 1–5 in PHP — somewhere
 *  upstream the keys get reindexed. We tolerate either shape so the bars
 *  stay correct if the backend is fixed later. */
function normalizeRatingCounts(raw) {
  const blank = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  if (!raw) return blank;
  // Zero-indexed array (current API shape): index 0 = 1★, index 4 = 5★.
  if (Array.isArray(raw)) {
    return {
      1: Number(raw[0]) || 0,
      2: Number(raw[1]) || 0,
      3: Number(raw[2]) || 0,
      4: Number(raw[3]) || 0,
      5: Number(raw[4]) || 0,
    };
  }
  // Object — accept numeric or string keys, prefer keys 1–5.
  return {
    1: Number(raw[1] ?? raw['1']) || 0,
    2: Number(raw[2] ?? raw['2']) || 0,
    3: Number(raw[3] ?? raw['3']) || 0,
    4: Number(raw[4] ?? raw['4']) || 0,
    5: Number(raw[5] ?? raw['5']) || 0,
  };
}

/** `.store-card` (594–600). */
function StoreCard({ storeKey, storeName, brandIcon, data, loading }) {
  const totalReviews = Number(data?.total_reviews) || 0;
  // Google sometimes returns an approximate display string ("11.5K") that's
  // a better representation than the integer count.
  const totalDisplay = data?.total_reviews_display || formatNum(totalReviews);
  const avgRating = Number(data?.average_rating);
  const counts = normalizeRatingCounts(data?.rating_counts);
  const safeCount = (s) => counts[s] || 0;
  const peak = Math.max(1, ...[1, 2, 3, 4, 5].map(safeCount));

  // Sample-scope label. The two stores expose very different windows:
  //   • Apple — only the latest batch returned by the RSS feed (~50 reviews
  //              in practice), so the histogram is a snapshot.
  //   • Google — the full corpus pulled from the public store details page.
  // We classify with a 90% threshold instead of hard-coding by store so the
  // label stays correct if either backend changes its behavior.
  const histogramSum = safeCount(1) + safeCount(2) + safeCount(3) +
                       safeCount(4) + safeCount(5);
  const isFullSample = totalReviews > 0 && histogramSum / totalReviews >= 0.9;
  const scopeLabel   = isFullSample ? 'All-time distribution' : 'Recent ratings';
  const scopeTooltip = isFullSample
    ? `Distribution across all ${formatNum(totalReviews)} reviews.`
    : 'Reflects the latest batch returned by the store, not the full review history.';
  const reviews = Array.isArray(data?.reviews) ? data.reviews.slice(0, 5) : [];

  return (
    <div
      className="bg-surface border border-sand min-w-0"
      style={{ borderRadius: 12, padding: '18px 20px' }}
    >
      {/* `.store-head` (601–627). */}
      <div className="flex items-center" style={{ gap: 10, marginBottom: 14 }}>
        {/* `.store-icon` (605–612): 28×28 wrapper, no bg, navy color,
         *  inner brand icon 22 (or 20 for google). */}
        <span
          className="grid place-items-center shrink-0 text-navy"
          style={{ width: 28, height: 28 }}
        >
          <BrandIcon name={brandIcon} size={storeKey === 'google' ? 20 : 22} />
        </span>
        <h4
          className="font-condensed font-bold text-navy flex-1"
          style={{ fontSize: 14 }}
        >
          {storeName}
        </h4>
        {/* `.store-link` (619–627). */}
        <a
          href={STORE_URLS[storeKey]}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'font-condensed font-bold uppercase no-underline',
            'inline-flex items-center text-teal-green-dark hover:text-teal-green',
          )}
          style={{ fontSize: 10.5, letterSpacing: '0.08em', gap: 4 }}
        >
          Open →
        </a>
      </div>

      {/* `.store-rating` (628–648): big rating, stars, count, with bottom
       *  divider. */}
      <div
        className="flex items-center border-b border-sand"
        style={{ gap: 14, paddingBottom: 14, marginBottom: 14 }}
      >
        <div
          className="font-condensed font-bold text-navy"
          style={{ fontSize: 42, letterSpacing: '-0.02em', lineHeight: 1 }}
        >
          {loading ? '…' : formatRating(avgRating)}
        </div>
        <div className="min-w-0">
          <StarsText rating={avgRating} />
          <div className="text-text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {loading ? '…' : `${totalDisplay} reviews`}
          </div>
        </div>
      </div>

      {/* Distribution bars — additive on top of the prototype. Uses the
       *  `rating_counts` map the API already returns.
       *
       *  Scope caption above the bars makes the sample window explicit so
       *  the Apple histogram (recent ratings) isn't compared apples-to-
       *  oranges with Google's (full-history). */}
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 6, gap: 8 }}
      >
        {/* Eyebrow label + help icon. Tooltip scopes to the icon only so the
         *  bubble aligns under the `?`, not under the middle of the whole
         *  eyebrow phrase. */}
        <span
          className="font-condensed font-bold uppercase text-text-muted inline-flex items-center"
          style={{ fontSize: 10.5, letterSpacing: '0.08em', gap: 4 }}
        >
          {scopeLabel}
          <Tooltip content={scopeTooltip} side="top">
            <span
              className="inline-flex cursor-help"
              tabIndex={0}
              aria-label={scopeTooltip}
            >
              <BrandIcon name="info" size={11} />
            </span>
          </Tooltip>
        </span>
        <span
          className="font-mono text-text-muted"
          style={{ fontSize: 11 }}
        >
          {loading ? '…' : `${formatNum(histogramSum)} ratings`}
        </span>
      </div>
      <div style={{ marginBottom: 14 }}>
        {[5, 4, 3, 2, 1].map((stars) => {
          const c = safeCount(stars);
          const width = `${(c / peak) * 100}%`;
          return (
            <div
              key={stars}
              className="flex items-center"
              style={{ gap: 8, fontSize: 11, marginBottom: 3 }}
            >
              <span
                className="font-mono text-text-muted text-right"
                style={{ width: 12 }}
              >
                {stars}
              </span>
              <div
                className="flex-1 bg-cream rounded-pill overflow-hidden"
                style={{ height: 6 }}
              >
                <div
                  className="h-full bg-teal-green rounded-pill"
                  style={{ width, transition: 'width 220ms ease' }}
                />
              </div>
              <span
                className="font-mono text-text-muted text-right"
                style={{ width: 48 }}
              >
                {formatNum(c)}
              </span>
            </div>
          );
        })}
      </div>

      {/* `.reviews-label` (649–655) + `.review-list` (656–685). */}
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 10 }}
      >
        Recent Reviews
      </div>
      {reviews.length === 0 ? (
        <div className="text-text-light italic" style={{ fontSize: 12 }}>
          {loading ? 'Loading reviews…' : 'No recent reviews.'}
        </div>
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {reviews.map((r, i) => (
            <ReviewItem key={`${r.author || 'anon'}-${r.date || i}-${i}`} review={r} />
          ))}
        </div>
      )}
    </div>
  );
}

/** `.review-item` (659–685): cream bg, rounded-md, padding 10 12. */
function ReviewItem({ review }) {
  return (
    <div
      className="bg-cream"
      style={{ padding: '10px 12px', borderRadius: 8 }}
    >
      {/* `.review-head` (664–667). */}
      <div
        className="flex items-center justify-between"
        style={{ gap: 8, marginBottom: 4 }}
      >
        <span
          className="font-semibold text-navy truncate min-w-0"
          style={{ fontSize: 12 }}
        >
          {review.author || 'Anonymous'}
        </span>
        <StarsText rating={review.rating} size={11} letterSpacing={0.5} />
      </div>
      {review.title && (
        <div
          className="font-semibold text-navy"
          style={{ fontSize: 12.5, marginBottom: 4 }}
        >
          {review.title}
        </div>
      )}
      {review.content && (
        // `.r-content` (676–680): 2-line clamp.
        <div
          className="text-text"
          style={{
            fontSize: 12,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {review.content}
        </div>
      )}
      {review.date && (
        <span
          className="block text-text-muted"
          style={{ fontSize: 10.5, marginTop: 4 }}
        >
          {formatReviewDate(review.date)}
        </span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════════════ */

export default function DashboardPageV2() {
  const dispatch = useDispatch();
  const { stats, appStore, loadingStats, loadingAppStore, error } = useSelector(
    (state) => state.dashboard,
  );
  const user = SessionHelper.currentUser();

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchAppStoreRatings());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchDashboardStats());
    dispatch(fetchAppStoreRatings());
  };
  const isLoading = loadingStats || loadingAppStore;

  const cs = stats?.content_statistics || {};

  return (
    // `.dash-body` (332–336): padding 24 28 32, bg.
    <div
      className="bg-bg min-h-screen"
      style={{ padding: '24px 28px 32px' }}
    >
      {/* `.dash-greeting` (337–358). */}
      <div className="flex items-end justify-between" style={{ marginBottom: 24, gap: 16 }}>
        <div className="min-w-0">
          {/* h1: Asap Condensed 700 / 34px / lh 1.05 / letter-spacing -0.02em. */}
          <h1
            className="font-condensed font-bold text-navy"
            style={{ fontSize: 34, lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            {greeting()},{' '}
            <em className="italic font-bold text-coral">{firstName(user)}</em>
          </h1>
          <div className="text-text-light" style={{ marginTop: 6, fontSize: 14 }}>
            Here&apos;s an overview of your content and how members are using it.
          </div>
        </div>
        {/* Right cluster — date strip + refresh button. */}
        <div className="flex items-center shrink-0" style={{ gap: 12 }}>
          {/* .date — 12/600 uppercase, letter-spacing 0.1em, text-muted. */}
          <span
            className="font-condensed font-semibold uppercase text-text-muted"
            style={{ fontSize: 12, letterSpacing: '0.1em' }}
          >
            {today()}
          </span>
          {/* Manual refresh — refetch dashboardStats + appStoreRatings.
           *  Icon counter-rotates a touch while loading for feedback. */}
          <IconButton
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh dashboard"
            title="Refresh"
            className={cn(isLoading && 'opacity-60')}
          >
            <span
              style={{
                display: 'inline-flex',
                animation: isLoading ? 'spin 900ms linear infinite' : 'none',
              }}
            >
              {RefreshIcon}
            </span>
          </IconButton>
        </div>
      </div>

      {error && (
        <div
          className="bg-coral-soft border border-coral text-coral-dark"
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* `.hero-row` (416–421): grid repeat(3, 1fr), gap 16, margin-top 22.
       *  Stacks to 1 column below 1080px. */}
      <div
        className="grid min-[1080px]:grid-cols-3 grid-cols-1"
        style={{ gap: 16, marginTop: 22 }}
      >
        <CompletedMeditationsCard data={stats?.completed_meditations} loading={loadingStats} />
        <MostPlayedCard data={stats?.most_played} loading={loadingStats} />
        <ActiveUsersCard value={stats?.active_users} loading={loadingStats} />
      </div>

      {/* Available Content */}
      <SectionTitle>Available Content</SectionTitle>
      <div
        className={cn(
          'grid grid-cols-1',
          'min-[640px]:grid-cols-2 min-[1080px]:grid-cols-3',
        )}
        style={{ gap: 14 }}
      >
        <ContentCard label="Sessions"      icon="sessions"    value={cs.sessions}      loading={loadingStats} />
        <ContentCard label="Quick Taps"    icon="quick_taps"  value={cs.quick_taps}    loading={loadingStats} />
        <ContentCard label="Challenges"    icon="challenges"  value={cs.challenges}    loading={loadingStats} />
        <ContentCard label="Series"        icon="series"      value={cs.series}        loading={loadingStats} />
        <ContentCard label="Categories"    icon="categories"  value={cs.categories}    loading={loadingStats} />
        <ContentCard label="Subcategories" icon="subcategory" value={cs.subcategories} loading={loadingStats} />
      </div>

      {/* App Store Reviews */}
      <SectionTitle>App Store Reviews</SectionTitle>
      <div
        className="grid grid-cols-1 min-[1080px]:grid-cols-2"
        style={{ gap: 16 }}
      >
        <StoreCard
          storeKey="apple"
          storeName="App Store"
          brandIcon="apple_logo"
          data={appStore?.apple}
          loading={loadingAppStore}
        />
        <StoreCard
          storeKey="google"
          storeName="Google Play"
          brandIcon="google_logo"
          data={appStore?.google}
          loading={loadingAppStore}
        />
      </div>
    </div>
  );
}
